import { useCallback, useEffect, useMemo, useState } from 'react';
import { Badge, Button } from '@mantine/core';
import logger from '@ayocore/platform/common/helpers/logger';
import { useRepositoryFilter } from '@ayocore/platform/client/components/RepositoryTable/useRepositoryFilter';
import type { PartnerRepository } from '@ayocore/erp/crm/server/repositories/PartnerRepository';
import type { AggregateItem } from '@ayocore/platform/common/types/aggregate';
import type { Partner } from '@ayocore/erp/crm/schemas/partner.schema';

type TaskStatusValue = 'overdue' | 'today' | 'tomorrow' | 'no_tasks' | 'no_activity';

const OPTIONS: Array<{ value: TaskStatusValue; label: string; badgeColor: string }> = [
  { value: 'overdue', label: 'Просроченные', badgeColor: 'red' },
  { value: 'today', label: 'Сегодня', badgeColor: 'orange' },
  { value: 'tomorrow', label: 'Завтра', badgeColor: 'violet' },
  { value: 'no_tasks', label: 'Без задач', badgeColor: 'orange' },
  { value: 'no_activity', label: 'Без движения', badgeColor: 'gray' },
];

const ALL_VALUES: ReadonlySet<TaskStatusValue> = new Set(OPTIONS.map(o => o.value));

// SUM по виртуальным флагам репозитория (см. PartnerRepository.withTaskFlags()).
// Cast `as keyof Partner` — фреймворк валидирует поле в runtime через __virtualColumnSql.
const TASK_AGGREGATES: AggregateItem<Partner>[] = [
  { fn: 'sum', field: 'is_task_overdue' as keyof Partner },
  { fn: 'sum', field: 'is_task_today' as keyof Partner },
  { fn: 'sum', field: 'is_task_tomorrow' as keyof Partner },
  { fn: 'sum', field: 'is_no_tasks' as keyof Partner },
  { fn: 'sum', field: 'is_no_activity' as keyof Partner },
];

function parseFromUrl(raw: string | null): TaskStatusValue[] {
  if (!raw) return [];
  return raw
    .split(',')
    .map(s => s.trim())
    .filter((s): s is TaskStatusValue => ALL_VALUES.has(s as TaskStatusValue));
}

function serializeForUrl(values: TaskStatusValue[]): string | null {
  return values.length === 0 ? null : values.join(',');
}

/**
 * Фильтр «Задачи» для PartnersList — мульти-выбор кнопок с бейджами-счётчиками:
 *   - Просроченные  — есть просроченная активная задача у партнёра
 *   - Без задач     — нет активных задач у партнёра
 *   - Без движения  — партнёр старше 5д, последняя задача и коммент — старше 5д
 *
 * Любая комбинация (OR-объединение). Счётчики учитывают остальные активные
 * фильтры таблицы — один SQL-запрос (`aggregate` поверх `withTaskFlags`).
 *
 * URL: `task_status=overdue,no_tasks`. Старое одиночное значение
 * (`task_status=overdue`) парсится как `['overdue']` — обратная совместимость
 * со старым Select-вариантом сохранена.
 */
export const FilterByTaskStatus = useRepositoryFilter<PartnerRepository>(
  'task_status',
  ({ filter, disabled, fromUrl, toUrl, subscribe, getFilteredRepository }) => {
    const [selected, setSelected] = useState<TaskStatusValue[]>([]);
    const selectedSet = useMemo(() => new Set(selected), [selected]);
    const [counts, setCounts] = useState<Record<TaskStatusValue, number>>({
      overdue: 0,
      today: 0,
      tomorrow: 0,
      no_tasks: 0,
      no_activity: 0,
    });

    const reloadCounts = useCallback(async () => {
      try {
        const result = await getFilteredRepository().withTaskFlags().aggregate(TASK_AGGREGATES);
        setCounts({
          overdue: Number(result.sum_is_task_overdue ?? 0),
          today: Number(result.sum_is_task_today ?? 0),
          tomorrow: Number(result.sum_is_task_tomorrow ?? 0),
          no_tasks: Number(result.sum_is_no_tasks ?? 0),
          no_activity: Number(result.sum_is_no_activity ?? 0),
        });
      } catch (error) {
        logger.warn('Partners FilterByTaskStatus: aggregate failed', error);
      }
    }, [getFilteredRepository]);

    useEffect(() => {
      void reloadCounts();
      const unsubscribe = subscribe(() => {
        void reloadCounts();
      });
      return unsubscribe;
    }, [subscribe, reloadCounts]);

    // Восстановление из URL (один раз при монтировании).
    useEffect(() => {
      try {
        const restored = parseFromUrl(fromUrl());
        if (restored.length > 0) {
          setSelected(restored);
          filter(repo => repo.filterByTaskStatusAny(restored));
        }
      } catch (error) {
        logger.warn('Partners FilterByTaskStatus: fromUrl failed', error);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleClick = (next: TaskStatusValue) => {
      const willSelect = selectedSet.has(next)
        ? selected.filter(v => v !== next)
        : [...selected, next];
      setSelected(willSelect);
      filter(willSelect.length > 0 ? repo => repo.filterByTaskStatusAny(willSelect) : null);
      toUrl(serializeForUrl(willSelect));
    };

    return (
      <Button.Group>
        {OPTIONS.map(option => {
          const active = selectedSet.has(option.value);
          return (
            <Button
              key={option.value}
              size='xs'
              variant={active ? 'filled' : 'default'}
              onClick={() => handleClick(option.value)}
              disabled={disabled}
              rightSection={
                <Badge size='xs' variant='filled' color={option.badgeColor}>
                  {counts[option.value]}
                </Badge>
              }
            >
              {option.label}
            </Button>
          );
        })}
      </Button.Group>
    );
  }
);
